// Groove sign-in can fail after Keycloak succeeds. These helpers keep that
// failure on the page: they never clear the session or replace the document.

export const GROOVE_SUPPORT_URL = 'http://support.groovedigital.com/';

const ACCOUNT_NOT_FOUND = 'No Groove account matches this login.';
const EMAIL_MISSING = "The sign-in didn't share an email address.";

export function isKeycloakTokenLogin(url) {
  return String(url || '').indexOf('keycloak-token-login') !== -1;
}

export function isAppOrSiteRequest(url) {
  if (isKeycloakTokenLogin(url)) return false;
  return /\/sites(?:\/|$|\?)|\/apps(?:\/|$|\?)|\/applications(?:\/|$|\?)/i.test(String(url || ''));
}

function responseBody(error) {
  const data = error && error.response && error.response.data;
  if (data && typeof data === 'object') return data;
  return { message: typeof data === 'string' ? data : '' };
}

export function describeKeycloakLoginFailure(error) {
  const body = responseBody(error);
  const code = body.code || body.error || body.error_code || '';
  const apiMessage = typeof body.message === 'string' ? body.message.trim() : '';

  if (code === 'account_not_found') {
    return {
      code,
      title: 'No Groove account matches this login',
      message: apiMessage || ACCOUNT_NOT_FOUND,
      detail: apiMessage && apiMessage.indexOf(ACCOUNT_NOT_FOUND) === -1 ? ACCOUNT_NOT_FOUND : '',
      supportUrl: GROOVE_SUPPORT_URL,
    };
  }

  if (code === 'email_missing') {
    return {
      code,
      title: 'Email address not shared',
      message: apiMessage || EMAIL_MISSING,
      detail: apiMessage && apiMessage.indexOf(EMAIL_MISSING) === -1 ? EMAIL_MISSING : '',
      supportUrl: '',
    };
  }

  return {
    code,
    title: 'Sign-in could not be completed',
    message: apiMessage || 'Sign-in could not be completed.',
    detail: '',
    supportUrl: '',
  };
}

export function describeAppSiteFailure(error) {
  const status = error && error.response ? error.response.status : 0;
  const apiMessage = typeof responseBody(error).message === 'string'
    ? responseBody(error).message.trim()
    : '';
  if (status === 401) {
    return {
      status,
      message: apiMessage || 'Applications could not be loaded. Sign out and try again.',
    };
  }
  if (status >= 500) {
    return {
      status,
      message: apiMessage || 'Applications could not be loaded. Please try again.',
    };
  }
  return {
    status,
    message: apiMessage || 'Applications could not be loaded.',
  };
}

export function publishBootstrap(partial) {
  if (typeof window === 'undefined') return partial;
  window.__gdBootstrap = Object.assign({}, window.__gdBootstrap || {}, partial);
  return window.__gdBootstrap;
}

export function readAppSiteRequestError(vm) {
  if (vm && vm.appSiteRequestError) return vm.appSiteRequestError;
  if (typeof window !== 'undefined' && window.__gdBootstrap && window.__gdBootstrap.appSiteRequestError) {
    return window.__gdBootstrap.appSiteRequestError;
  }
  return null;
}

// A live Keycloak session plus an API 401 is not proof the session died.
// Clearing Vuex first is what left app.groove.cm as an empty document.
export function settleUnauthorized(error, hooks) {
  const url = (error && error.config && error.config.url) || '';
  const status = error && error.response ? error.response.status : 0;

  if (isAppOrSiteRequest(url) && (status === 401 || status >= 500)) {
    hooks.recordAppSiteFailure(describeAppSiteFailure(error));
  }

  if (status === 401 && isKeycloakTokenLogin(url)) {
    hooks.recordLoginFailure(describeKeycloakLoginFailure(error));
    return Promise.resolve('show-login-failure');
  }

  if (status !== 401) return Promise.resolve('reject');

  if (hooks.isAuthenticated()) {
    return Promise.resolve()
      .then(() => hooks.refresh())
      .then((ok) => {
        if (!ok) {
          hooks.logout();
          return 'logout';
        }
        return 'keep-session';
      });
  }

  hooks.clearSession();
  hooks.redirectToLogin();
  return Promise.resolve('login-redirect');
}

export function missingGrooveUserId(user, authenticated) {
  if (!authenticated) return false;
  if (!user || typeof user !== 'object') return true;
  return user.id == null || String(user.id).trim() === '';
}

export function isLiteGrooveUser(user) {
  if (!user || typeof user !== 'object') return false;
  return (
    (user.roles && user.roles.indexOf('groovepages_user') === -1) ||
    (user.plan && Array.isArray(user.plan) && user.plan.length > 0 && user.plan[0].toLowerCase() === 'startup')
  );
}

export function grooveUserMetadata(user) {
  if (!user || typeof user !== 'object') return undefined;
  return user.metadata;
}

export function renderBootstrapFailure(h, vm) {
  const failure = vm.failure || {};
  const card = [
    h('h1', failure.title || 'Sign-in could not be completed'),
    h('p', failure.message || 'Sign-in could not be completed.'),
  ];
  if (failure.detail && failure.detail !== failure.message) {
    card.push(h('p', failure.detail));
  }
  if (failure.supportUrl) {
    card.push(h('p', [
      h('a', {
        attrs: { href: failure.supportUrl, target: '_blank', rel: 'noopener noreferrer' },
      }, 'Contact support'),
    ]));
  }
  card.push(h('div', { class: 'gd-bootstrap-failure__actions' }, [
    h('button', {
      attrs: { type: 'button', disabled: vm.busy ? true : null },
      on: { click: () => { if (!vm.busy) vm.$emit('retry'); } },
    }, 'Retry'),
    h('button', {
      attrs: { type: 'button' },
      on: { click: () => vm.$emit('sign-out') },
    }, 'Sign out'),
  ]));
  return h('section', {
    class: 'gd-bootstrap-failure',
    attrs: { role: 'alert' },
  }, [
    h('div', { class: 'gd-bootstrap-failure__card' }, card),
  ]);
}

export function createBootstrapFailure() {
  return {
    name: 'BootstrapFailure',
    props: {
      failure: { type: Object, default: () => ({}) },
      busy: { type: Boolean, default: false },
    },
    render(h) {
      return renderBootstrapFailure(h, this);
    },
  };
}

export function renderDashboardErrorFallback(h, vm) {
  if (!vm.error) {
    const slot = vm.$slots && vm.$slots.default;
    if (slot && slot.length === 1) return slot[0];
    return h('div', { class: 'gd-dashboard-error-boundary' }, slot || []);
  }
  return h('div', { class: 'gd-dashboard-error', attrs: { role: 'alert' } }, [
    h('div', { class: 'gd-dashboard-error__card' }, [
      h('h1', 'The dashboard could not be displayed'),
      h('p', vm.error.message || 'Something went wrong while loading the dashboard.'),
      h('button', {
        attrs: { type: 'button' },
        on: { click: vm.reload },
      }, 'Reload'),
    ]),
  ]);
}

export function createDashboardErrorBoundary() {
  return {
    name: 'DashboardErrorBoundary',
    data: () => ({ error: null }),
    errorCaptured(error, vm, info) {
      if (!info || String(info).indexOf('render') === -1) return undefined;
      this.error = error instanceof Error ? error : new Error('The dashboard could not be displayed.');
      return false;
    },
    methods: {
      reload() {
        if (typeof window !== 'undefined' && window.location && window.location.reload) {
          window.location.reload();
        }
      },
    },
    render(h) {
      return renderDashboardErrorFallback(h, this);
    },
  };
}
